<template>
  <div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div
            class="col-lg-9 com-md-9 col-sm-12 d-flex justify-content-start"
          ></div>
          <div class="col-lg-3 com-md-3 col-sm-12 d-flex justify-content-end">
            <ts-input
              v-model="search"
              :placeholder="$t('Search')"
              class="kh-font"
            />
          </div>
        </div>
      </ts-card-wrapper>

      <div style="overflow-x: auto;">
        <table class="table table-hover ts-table" v-if="!loading">
          <thead>
            <tr>
              <th style="width:40px;"></th>
              <th style="text-align:left;">
                {{ $t('paymentannouncement.startDate') }}
              </th>
              <th style="text-align:left;">
                {{ $t('paymentannouncement.dueDate') }}
              </th>
              <th style="text-align:left;">
                {{ $t('paymentannouncement.endDate') }}
              </th>
              <th style="text-align:left;">
                {{ $t('paymentannouncement.createdDate') }}
              </th>
              <th style="text-align:left;">
                {{ $t('paymentannouncement.modifyDate') }}
              </th>
              <th style="text-align:center; width:10%;">{{ $t('actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="record in uniqueResources">
              <!-- Main Row -->
              <tr :key="'row-' + record.anc_id">
                <td>
                  <a
                    href="#"
                    class="text-secondary"
                    @click.prevent="toggleExpand(record)"
                    v-tooltip="
                      expandedRows.includes(record.anc_id)
                        ? 'Collapse'
                        : 'Expand'
                    "
                  >
                    <i
                      class="fas"
                      :class="
                        expandedRows.includes(record.anc_id)
                          ? 'fa-chevron-down'
                          : 'fa-chevron-right'
                      "
                    ></i>
                  </a>
                </td>
                <td>{{ record.start_date }}</td>
                <td>{{ record.payment_due_date }}</td>
                <td>{{ record.end_date }}</td>
                <td>{{ record.created_date }}</td>
                <td>{{ record.modified_date }}</td>
                <td class="text-center">
                  <a
                    href="#"
                    v-tooltip="'Edit'"
                    class="mr-2 text-primary"
                    @click.prevent="edit(record)"
                  >
                    <i class="fas fa-edit"></i>
                  </a>
                  <a
                    href="#"
                    v-tooltip="'Delete'"
                    class="text-danger"
                    @click.prevent="destroy(record)"
                    :disabled="record._deleting"
                  >
                    <i
                      class="fas fa-circle-notch spin"
                      v-if="record._deleting"
                    ></i>
                    <i class="fas fa-trash" v-else></i>
                  </a>
                </td>
              </tr>

              <!-- Expand Row -->
              <tr
                :key="'expand-' + record.anc_id"
                v-if="expandedRows.includes(record.anc_id)"
                class="expand-row"
              >
                <td :colspan="7" class="expand-cell">
                  <table-expand :record="record" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <!-- Loading state -->
        <div v-if="loading" class="text-center p-20">
          <i class="fas fa-spinner spin fa-2x text-muted"></i>
        </div>

        <!-- Empty state -->
        <div
          v-if="!loading && !uniqueResources.length"
          class="text-center p-20 text-muted"
        >
          {{ $t('noData') }}
        </div>
      </div>

      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { debounce } from 'lodash'
import TableExpand from './table-expand.vue'

export default {
  props: ['fetch'],
  name: 'payment-announcement-table',
  components: { TableExpand },
  data() {
    return {
      loading: false,
      expandedRows: []
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('paymentAnnouncement', ['resources', 'pagination']),
    uniqueResources() {
      const seen = new Set()
      return this.resources.filter(r => {
        if (seen.has(r.anc_id)) return false
        seen.add(r.anc_id)
        return true
      })
    },
    search: {
      get() {
        return this.$store.state.paymentAnnouncement.search
      },
      set(newValue) {
        this.$store.commit('paymentAnnouncement/SET_SEARCH', newValue)
        this.$store.commit('paymentAnnouncement/RESET_CURRENT_PAGE')
      }
    }
  },
  methods: {
    toggleExpand(record) {
      const id = record.anc_id
      if (this.expandedRows.includes(id)) {
        this.expandedRows = this.expandedRows.filter(r => r !== id)
      } else {
        this.expandedRows.push(id)
      }
    },
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('paymentAnnouncement/fetch', attributes)
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    edit(record) {
      this.$store.commit('paymentAnnouncement/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'payment-announcement-edit',
        params: { id: record.anc_id }
      })
    },
    async destroy(record) {
      if (await this.$confirmModal.danger('Are you sure to delete ?')) {
        record._deleting = true
        this.$store
          .dispatch('paymentAnnouncement/destroy', record.anc_id)
          .then(response => {
            this.$toasted.success(response.message)
            this.fetchData()
            record._deleting = false
          })
          .catch(error => {
            this.$toasted.error(error.message)
            record._deleting = false
          })
      }
    }
  },
  watch: {
    search: debounce(function(search) {
      this.fetchData({ search })
    }, 500),
    fetch: function() {
      this.fetchData()
      this.$emit('fetched')
    }
  }
}
</script>

<style scoped>
.ts-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.ts-table thead tr {
  background-color: #f5f5f5;
}

.ts-table th {
  padding: 10px 12px;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #e0e0e0;
  white-space: nowrap;
}

.ts-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.ts-table tbody tr:hover {
  background-color: #fafafa;
}

.expand-row {
  background-color: #f8f9fa !important;
}

.expand-row:hover {
  background-color: #f8f9fa !important;
}

.expand-cell {
  padding: 12px 24px !important;
  border-top: none !important;
}

.text-center {
  text-align: center;
}

.p-20 {
  padding: 20px;
}
</style>
